import React, { useEffect, useState } from "react";
import { Plus, Edit3, Trash2, Save, X, Shield, ClipboardCheck } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const ROLE_META = {
  admin: { label: "Organisateur", color: "bg-[#E60012]/20 text-[#E60012] border-[#E60012]/40", icon: Shield },
  marker: { label: "Marqueur", color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", icon: ClipboardCheck },
};

const UsersPanel = ({ currentUserId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | user object
  const [error, setError] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const onDelete = async (u) => {
    if (!window.confirm(`Supprimer le compte de ${u.name} (${u.email}) ?`)) return;
    setError("");
    try {
      await api.delete(`/admin/users/${u.id}`);
      refresh();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="admin-users-panel">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">Sécurité</span>
          <h2 className="mt-1 font-display text-4xl uppercase">Utilisateurs</h2>
          <p className="mt-1 text-white/55 text-sm max-w-2xl">
            Gérez les comptes des organisateurs (accès complet) et marqueurs (alignements + scores +
            statistiques uniquement).
          </p>
        </div>
        <button
          onClick={() => setEditing("new")}
          data-testid="users-new-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#E60012] text-white font-condensed uppercase tracking-[0.2em] text-xs hover:brightness-110"
        >
          <Plus className="w-4 h-4" /> Nouveau compte
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-condensed">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-white/40 font-condensed uppercase tracking-[0.2em] text-xs">
          Chargement…
        </div>
      ) : (
        <div className="bg-[#111] border border-white/10 overflow-hidden">
          <div className="hidden md:grid grid-cols-[1fr_1.6fr_140px_120px] gap-3 px-5 py-3 bg-black/40 text-[10px] font-condensed uppercase tracking-[0.2em] text-white/55">
            <div>Nom</div>
            <div>Courriel</div>
            <div>Rôle</div>
            <div className="text-right">Actions</div>
          </div>
          {users.length === 0 ? (
            <div className="p-8 text-center text-white/40 font-condensed text-sm">Aucun utilisateur.</div>
          ) : (
            users.map((u, idx) => {
              const meta = ROLE_META[u.role] || { label: u.role, color: "bg-white/10 text-white", icon: Shield };
              const isSelf = u.id === currentUserId;
              return (
                <div
                  key={u.id}
                  className={`grid grid-cols-[1fr_auto] md:grid-cols-[1fr_1.6fr_140px_120px] gap-3 items-center px-5 py-4 border-t border-white/5 ${idx % 2 === 0 ? "bg-[#0c0c0c]" : "bg-[#141414]"}`}
                  data-testid={`user-row-${u.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-condensed text-white truncate">
                      {u.name}
                      {isSelf && <span className="ml-2 text-[10px] text-emerald-400 uppercase tracking-wider">Vous</span>}
                    </div>
                    <div className="md:hidden text-xs text-white/55 truncate">{u.email}</div>
                  </div>
                  <div className="hidden md:block text-sm text-white/70 truncate">{u.email}</div>
                  <div>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 border ${meta.color} text-[10px] uppercase tracking-[0.2em] font-condensed`}>
                      <meta.icon className="w-3 h-3" /> {meta.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 justify-end">
                    <button
                      onClick={() => setEditing(u)}
                      data-testid={`user-edit-${u.id}`}
                      className="p-2 border border-white/15 hover:border-[#E60012] hover:text-[#E60012]"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onDelete(u)}
                      disabled={isSelf}
                      data-testid={`user-delete-${u.id}`}
                      className={`p-2 border border-white/15 ${isSelf ? "opacity-30 cursor-not-allowed" : "hover:border-red-500 hover:text-red-400"}`}
                      title={isSelf ? "Vous ne pouvez pas supprimer votre propre compte" : ""}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {editing && (
        <UserModal
          mode={editing === "new" ? "create" : "edit"}
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
};

const UserModal = ({ mode, initial, onClose, onSaved }) => {
  const [name, setName] = useState(initial?.name || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [role, setRole] = useState(initial?.role || "marker");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isCreate = mode === "create";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) return setError("Le nom est requis.");
    if (!email.trim()) return setError("Le courriel est requis.");
    if (isCreate || password) {
      if (password.length < 8) return setError("Le mot de passe doit avoir au moins 8 caractères.");
      if (password !== confirm) return setError("Les mots de passe ne correspondent pas.");
    }

    setSaving(true);
    try {
      const payload = { name: name.trim(), email: email.trim(), role };
      if (password) payload.password = password;
      if (isCreate) {
        await api.post("/admin/users", payload);
      } else {
        await api.put(`/admin/users/${initial.id}`, payload);
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        className="bg-[#111] border border-white/15 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="user-modal"
      >
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-display text-2xl uppercase">
            {isCreate ? "Nouveau compte" : "Modifier le compte"}
          </h3>
          <button type="button" onClick={onClose} className="p-1 hover:text-[#E60012]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <Field label="Nom complet">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="user-modal-name"
              className="w-full bg-black/40 border border-white/15 px-4 py-2.5 font-condensed focus:outline-none focus:border-[#E60012]"
            />
          </Field>
          <Field label="Courriel">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="user-modal-email"
              className="w-full bg-black/40 border border-white/15 px-4 py-2.5 font-condensed focus:outline-none focus:border-[#E60012]"
            />
          </Field>
          <Field label="Rôle">
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(ROLE_META).map(([id, meta]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setRole(id)}
                  data-testid={`user-modal-role-${id}`}
                  className={`px-3 py-2.5 border text-left transition-colors ${
                    role === id
                      ? "bg-[#E60012]/15 border-[#E60012] text-white"
                      : "border-white/15 text-white/60 hover:border-white/40"
                  }`}
                >
                  <div className="flex items-center gap-2 text-xs font-condensed uppercase tracking-wider">
                    <meta.icon className="w-3.5 h-3.5" /> {meta.label}
                  </div>
                  <div className="mt-1 text-[10px] text-white/40 leading-snug">
                    {id === "admin"
                      ? "Accès complet à tout le panneau."
                      : "Alignements, scores et statistiques uniquement."}
                  </div>
                </button>
              ))}
            </div>
          </Field>
          <Field label={isCreate ? "Mot de passe" : "Nouveau mot de passe (optionnel)"}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isCreate ? "" : "Laissez vide pour ne pas changer"}
              minLength={isCreate ? 8 : undefined}
              data-testid="user-modal-password"
              className="w-full bg-black/40 border border-white/15 px-4 py-2.5 font-condensed focus:outline-none focus:border-[#E60012]"
            />
          </Field>
          {(isCreate || password) && (
            <Field label="Confirmer le mot de passe">
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="user-modal-confirm"
                className="w-full bg-black/40 border border-white/15 px-4 py-2.5 font-condensed focus:outline-none focus:border-[#E60012]"
              />
            </Field>
          )}

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-condensed">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-white/15 hover:border-white/40 font-condensed uppercase tracking-[0.2em] text-xs"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="user-modal-save"
            className="inline-flex items-center gap-2 px-5 py-2 bg-[#E60012] text-white font-condensed uppercase tracking-[0.2em] text-xs hover:brightness-110 disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] font-condensed uppercase tracking-[0.25em] text-white/55 mb-1.5">
      {label}
    </div>
    {children}
  </label>
);

export default UsersPanel;
